import Color from "color";

import {
  type PartialGroup,
  type PartialUser,
  initial,
  userInitials,
} from "metabase/common/utils/user";
import { type MantineTheme, useMantineTheme } from "metabase/ui";
import type { ColorName } from "metabase/ui/colors/types";
import {
  color,
  isColorName,
  resolveColorFromCssVariable,
} from "metabase/ui/utils/colors";

import type { AvatarProps } from "./UserAvatar.styled";
import { Avatar as StyledAvatar } from "./UserAvatar.styled";

const DEFAULT_BACKGROUND_COLOR = color("core-brand");
const TEXT_COLOR_CANDIDATES = [
  "text-primary",
  "text-primary-inverse",
] as const satisfies readonly ColorName[];

interface UserAvatarProps extends AvatarProps {
  user: PartialUser | PartialGroup;
}

function resolveThemeColor(
  colorValue: string,
  getColor: (colorName: ColorName) => string,
) {
  if (isColorName(colorValue)) {
    return getColor(colorValue);
  }

  return resolveColorFromCssVariable(colorValue, (colorName) =>
    isColorName(colorName) ? getColor(colorName) : colorName,
  );
}

export function getAvatarTextColor(
  backgroundColor: string,
  getColor: (colorName: ColorName) => string,
) {
  const resolvedBackgroundColor = Color(
    resolveThemeColor(backgroundColor, getColor),
  );

  return TEXT_COLOR_CANDIDATES.map((colorName) =>
    Color(getColor(colorName)).alpha(1),
  )
    .reduce((bestColor, candidateColor) =>
      candidateColor.contrast(resolvedBackgroundColor) >
      bestColor.contrast(resolvedBackgroundColor)
        ? candidateColor
        : bestColor,
    )
    .string();
}

function ContrastSafeAvatar({
  bg = DEFAULT_BACKGROUND_COLOR,
  children,
  ...props
}: AvatarProps & { children: string }) {
  const theme: MantineTheme = useMantineTheme();
  const textColor = getAvatarTextColor(bg, theme.fn.themeColor);

  return (
    <StyledAvatar {...props} bg={bg} $textColor={textColor}>
      {children}
    </StyledAvatar>
  );
}

export function UserAvatar({ user, ...props }: UserAvatarProps) {
  return (
    <ContrastSafeAvatar {...props}>
      {userInitials(user) || "?"}
    </ContrastSafeAvatar>
  );
}

export function Avatar({
  children,
  ...props
}: AvatarProps & { children: string }) {
  return (
    <ContrastSafeAvatar {...props}>
      {initial(children) ?? "?"}
    </ContrastSafeAvatar>
  );
}
